<template>
  <div class="white-bg sub-section">
    <div class="sub-section-header">
      <a @click="$emit('hide')">
        <font-awesome-icon
          :icon="['fas', 'arrow-left']"
          size="lg"
          style="margin-bottom: 0.3rem;"
        />
      </a>
      &nbsp;&nbsp;
      <h3 style="display: inline-block;">
        {{ $t('account.acctDetails.security') }}
      </h3>
    </div>

    <!-- Password section -->
    <div class="mt-4">
      <h5>{{ $t('account.security.passwd') }}</h5>
      <hr class="mt-1 mb-4">
      <b-row>
        <b-col cols="12" lg="4" class="mb-4">
          <span>{{ $t('account.security.passwdText') }}</span>
        </b-col>
        <b-col cols="12" lg="8">
          <!-- Old password -->
          <b-form-group>
            <label for="actualPassword">{{ $t('account.security.actualPasswd') }}</label>
            <b-form-input
              id="actualPassword"
              type="password"
              name="actualPassword"
              v-model="actualPassword"
              autocomplete="new-password"
            ></b-form-input>
          </b-form-group>

          <!-- New password -->
          <b-form-group>
            <label for="newPassword">{{ $t('account.security.newPasswd') }}</label>
            <b-form-input
              id="newPassword"
              type="password"
              name="newPassword"
              :state="newPasswordState"
              v-model="newPassword"
              autocomplete="new-password"
            ></b-form-input>
            <b-form-group class="mb-0" v-if="!newPasswordState">
              <b-form-invalid-feedback
                id="newPasswordFeedback"
                class="align-left"
                :state="newPasswordMinState"
              >
                {{ $t('account.security.newPasswdFeedMin') }}
              </b-form-invalid-feedback>
              <b-form-valid-feedback
                id="newPasswordFeedback"
                class="align-left"
                :state="newPasswordMinState"
              >
                {{ $t('account.security.newPasswdFeedMin') }}
              </b-form-valid-feedback>
              <b-form-invalid-feedback
                id="newPasswordUpperCaseFeedback"
                class="align-left"
                :state="newPasswordUpperCaseState"
              >
                {{ $t('account.security.newPasswdFeedUpper') }}
              </b-form-invalid-feedback>
              <b-form-valid-feedback
                id="newPasswordUpperCaseFeedback"
                class="align-left"
                :state="newPasswordUpperCaseState"
              >
                {{ $t('account.security.newPasswdFeedUpper') }}
              </b-form-valid-feedback>
              <b-form-invalid-feedback
                id="newPasswordLowerCaseFeedback"
                class="align-left"
                :state="newPasswordLowerCaseState"
              >
                {{ $t('account.security.newPasswdFeedLower') }}
              </b-form-invalid-feedback>
              <b-form-valid-feedback
                id="newPasswordLowerCaseFeedback"
                class="align-left"
                :state="newPasswordLowerCaseState"
              >
                {{ $t('account.security.newPasswdFeedLower') }}
              </b-form-valid-feedback>
              <b-form-invalid-feedback
                id="newPasswordNumberFeedback"
                class="align-left"
                :state="newPasswordNumberState"
              >
                {{ $t('account.security.newPasswdFeedNumber') }}
              </b-form-invalid-feedback>
              <b-form-valid-feedback
                id="newPasswordNumberFeedback"
                class="align-left"
                :state="newPasswordNumberState"
              >
                {{ $t('account.security.newPasswdFeedNumber') }}
              </b-form-valid-feedback>
              <b-form-invalid-feedback
                id="newPasswordFeedback"
                class="align-left"
                :state="newPasswordCharState"
              >
                {{ $t('account.security.newPasswdFeedChar') }}
              </b-form-invalid-feedback>
              <b-form-valid-feedback
                id="newPasswordFeedback"
                class="align-left"
                :state="newPasswordCharState"
              >
                {{ $t('account.security.newPasswdFeedChar') }}
              </b-form-valid-feedback>
            </b-form-group>
          </b-form-group>

          <b-form-group>
            <label for="confirmPassword">{{ $t('account.security.reNewPasswd') }}</label>
            <b-form-input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              :state="confirmPasswordState"
              v-model="confirmPassword"
              autocomplete="new-password"
              aria-describedby="reNewPasswdFeedback"
            ></b-form-input>
            <b-form-invalid-feedback
              id="reNewPasswdFeedback"
              class="align-left"
            >
              {{ $t('account.security.reNewPasswdFeedB') }}
            </b-form-invalid-feedback>
          </b-form-group>
          <div>
            <b-button class="alt-primary-button" :disabled="!checkPasswdFormState" @click="changePassword">
              {{ $t('save') }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Phone number section -->
    <div class="mt-5">
      <h5>{{ $t('phoneNumber') }}</h5>
      <hr class="mt-1 mb-4">
      <b-row>
        <b-col cols="12" lg="4" class="mb-4">
          <span>{{ $t('account.security.phoneNumText') }}</span>
        </b-col>
        <b-col cols="12" lg="8">
          <!-- Old password -->
          <b-form-group>
            <label for="phoneNum">{{ $t('phoneNumber') }}</label>
            <b-form-input
              id="phoneNum"
              type="number"
              name="text"
              :state="phoneState"
              size="sm"
              v-model="phone"
              aria-describedby="phoneNumFeedB"
            ></b-form-input>
            <b-form-invalid-feedback id="phoneNumFeedB">
              <!-- This will only be shown if the preceeding input has an invalid state -->
              {{ $t('account.security.phoneNumFeedB') }}
            </b-form-invalid-feedback>
          </b-form-group>
          <div>
            <b-button class="alt-primary-button" :disabled="!validPhoneForm" @click="changePhoneNum">
              {{ $t('save') }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import AccountMixin from '../../services/AccountMixin';
import { mapGetters } from 'vuex';

export default {
  name: 'LoginAndSecurity',
  data() {
    return {
      actualPassword: '',
      newPassword: '',
      confirmPassword: '',
      phone: '',
    };
  },

  mixins: [AccountMixin],

  created() {
    this.phone = this.actualPhone;
  },

  computed: {
    ...mapGetters({
      actualPhone: 'accountStore/getPhone'
    }),

    // Just check that actualPassword has something
    actualPasswordState() {
      if (this.actualPassword.length === 0) return null;
      return this.actualPassword.length > 0;
    },

    newPasswordState() {
      return this.getPasswordState(this.newPassword);
    },

    newPasswordMinState() {
      return this.getPasswordMinState(this.newPassword);
    },

    newPasswordUpperCaseState() {
      return this.getPasswordUpperCaseState(this.newPassword);
    },

    newPasswordLowerCaseState() {
      return this.getPasswordLowerCaseState(this.newPassword);
    },

    newPasswordNumberState() {
      return this.getPasswordNumberState(this.newPassword);
    },

    newPasswordCharState() {
      return this.getPasswordCharState(this.newPassword);
    },

    confirmPasswordState() {
      return this.getConfirmPasswordState(this.newPassword, this.confirmPassword);
    },

    phoneState() {
      return this.getPhoneState(this.phone);
    },

    checkPasswdFormState() {
      return this.actualPasswordState
        && this.newPasswordState
        && this.confirmPasswordState;
    },

    validPhoneForm() {
      return this.phoneState;
    }
  },

  methods: {
    validNewPasswdForm() {
      return this.checkPasswdFormState;
    },

    resetPasswdFields() {
      this.actualPassword = '';
      this.newPassword = '';
      this.confirmPassword = '';
    },

    async changePassword() {
      try {
        if(!this.validNewPasswdForm()) return;
        
        await this.$store.dispatch(
          'accountStore/changePassword',
          { actualPassword: this.actualPassword, newPassword: this.newPassword }
        );

        this.resetPasswdFields();
        this.$notify({
          group: 'all',
          type: 'success',
          text: this.$t('account.security.changePasswdOK'),
        });
      }
      catch (err) {
        console.log('Error', err);
        this.$notify({
          group: 'all',
          type: 'error',
          text: this.$t('account.security.changePasswdErr'),
        });
      }
    },

    async changePhoneNum() {
      try {
        if(!this.validPhoneForm) return;
        
        await this.$store.dispatch(
          'accountStore/changePhoneNum',
          { phone: this.phone }
        );

        this.$notify({
          group: 'all',
          type: 'success',
          text: this.$t('account.security.changePhoneNumOK'),
        });
      }
      catch (err) {
        console.log('Error', err);
        this.$notify({
          group: 'all',
          type: 'error',
          text: this.$t('account.security.changePhoneNumErr'),
        });
      }
    }
  }
}
</script>